import React from 'react';
import { Card, CardContent, Typography, TextField, MenuItem, Box, Button, IconButton, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import type { TreatmentData } from '@app-types/visit';
import { MedicalServices, Add, Delete } from '@mui/icons-material';

interface TreatmentsCardProps {
  treatments: TreatmentData[];
  onChange: (treatments: TreatmentData[]) => void;
  disabled?: boolean;
}

const TreatmentsCard: React.FC<TreatmentsCardProps> = React.memo(({ treatments, onChange, disabled = false }) => {
  const addTreatment = () => {
    const newTreatment: TreatmentData = {
      id: `treat-${Date.now()}`,
      drug: '',
      dose: '',
      route: 'oral',
      time: new Date().toTimeString().substring(0, 5),
      qty: 1,
      uom: 'buc',
      fromStock: false
    };
    onChange([...treatments, newTreatment]);
  };

  const updateTreatment = (id: string, updates: Partial<TreatmentData>) => {
    onChange(treatments.map(t => t.id === id ? { ...t, ...updates } : t));
  };

  const deleteTreatment = (id: string) => {
    onChange(treatments.filter(t => t.id !== id));
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MedicalServices sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Tratamente administrate în clinică</Typography>
          </Box>
          <Button
            startIcon={<Add />}
            onClick={addTreatment}
            disabled={disabled}
            size="small"
            variant="outlined"
          >
            Adaugă tratament
          </Button>
        </Box>
        
        <Stack spacing={2}>
          {treatments.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Nu există tratamente administrate. Apasă "Adaugă tratament" pentru a începe.
            </Typography>
          )}
          
          {treatments.map((treatment) => (
            <Box key={treatment.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
<Grid container spacing={2} alignItems="center">
<Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    required
                    label="Medicament"
                    value={treatment.drug}
                    onChange={(e) => updateTreatment(treatment.id, { drug: e.target.value })}
                    disabled={disabled}
                    error={!treatment.drug}
                  />
                </Grid>
                
<Grid size={{ xs: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    required
                    label="Doză"
                    value={treatment.dose}
                    onChange={(e) => updateTreatment(treatment.id, { dose: e.target.value })}
                    disabled={disabled}
                    error={!treatment.dose}
                  />
                </Grid>
                
<Grid size={{ xs: 6, md: 2 }}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Cale"
                    value={treatment.route}
                    onChange={(e) => updateTreatment(treatment.id, { route: e.target.value as TreatmentData['route'] })}
                    disabled={disabled}
                  >
                    <MenuItem value="oral">Oral</MenuItem>
                    <MenuItem value="iv">IV</MenuItem>
                    <MenuItem value="im">IM</MenuItem>
                    <MenuItem value="sc">SC</MenuItem>
                    <MenuItem value="topical">Topical</MenuItem>
                    <MenuItem value="other">Altă</MenuItem>
                  </TextField>
                </Grid>
                
<Grid size={{ xs: 4, md: 2 }}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Ora"
                    InputLabelProps={{ shrink: true }}
                    value={treatment.time}
                    onChange={(e) => updateTreatment(treatment.id, { time: e.target.value })}
                    disabled={disabled}
                  />
                </Grid>
                
<Grid size={{ xs: 4, md: 1 }}>
                  <TextField
                    fullWidth
                    type="number"
                    required
                    label="Qty"
                    value={treatment.qty}
                    onChange={(e) => updateTreatment(treatment.id, { qty: Number(e.target.value) })}
                    disabled={disabled}
                    inputProps={{ min: 0.01, step: 0.1 }}
                    error={treatment.qty <= 0}
                  />
                </Grid>
                
<Grid size={{ xs: 4, md: 1 }}>
                  <TextField
                    fullWidth
                    required
                    label="UOM"
                    value={treatment.uom}
                    onChange={(e) => updateTreatment(treatment.id, { uom: e.target.value })}
                    disabled={disabled}
                    error={!treatment.uom}
                  />
                </Grid>
                
<Grid size={{ xs: 6, md: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Din stoc"
                    value={treatment.fromStock ? 'yes' : 'no'}
                    onChange={(e) => updateTreatment(treatment.id, { fromStock: e.target.value === 'yes' })}
                    disabled={disabled}
                  >
                    <MenuItem value="no">Nu</MenuItem>
                    <MenuItem value="yes">Da</MenuItem>
                  </TextField>
                </Grid>
                
                {treatment.fromStock && (
                  <>
<Grid size={{ xs: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        required={treatment.fromStock}
                        label="Lot"
                        value={treatment.lot ?? ''}
                        onChange={(e) => updateTreatment(treatment.id, { lot: e.target.value })}
                        disabled={disabled}
                        error={treatment.fromStock && !treatment.lot}
                        helperText={treatment.fromStock ? "Obligatoriu" : ""}
                      />
                    </Grid>
                    
<Grid size={{ xs: 6, md: 2 }}>
                      <TextField
                        fullWidth
                        required={treatment.fromStock}
                        type="date"
                        label="Exp."
                        InputLabelProps={{ shrink: true }}
                        value={treatment.exp ?? ''}
                        onChange={(e) => updateTreatment(treatment.id, { exp: e.target.value })}
                        disabled={disabled}
                        error={treatment.fromStock && !treatment.exp}
                        helperText={treatment.fromStock ? "Obligatoriu" : ""}
                      />
                    </Grid>
                  </>
                )}
                
<Grid size={{ xs: 12, md: 1 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    color="error"
                    onClick={() => deleteTreatment(treatment.id)}
                    disabled={disabled}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
});

TreatmentsCard.displayName = 'TreatmentsCard';

export default TreatmentsCard;
